import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

export type AdSource = "rewarded" | `interstitial:${string}`;
export type InterstitialShowInput = { taskId: string; destinationVisible: boolean };
export type InterstitialAdapter = {
  show(input: InterstitialShowInput & { onImpression: () => void }): Promise<boolean>;
};
export type BannerStatus = "loading" | "rendered" | "no-fill" | "error" | "unavailable";
export type BannerAttachInput = {
  target: HTMLElement;
  taskId: string;
  placement: string;
  onStatus?: (status: BannerStatus) => void;
};
export type BannerAttachResult = { status: BannerStatus; destroy: () => void };
export type AdsAdapter = {
  interstitial: InterstitialAdapter;
  banner: { attach(input: BannerAttachInput): Promise<BannerAttachResult> };
  resetSession?: () => void;
  initialize?: () => Promise<boolean>;
};

export const noopAdsAdapter: AdsAdapter = {
  interstitial: { show: async () => false },
  banner: { attach: async () => ({ status: "unavailable", destroy: () => undefined }) },
};

type AdsContextValue = AdsAdapter & {
  isAnyAdOpen: boolean;
  openAd: (source: AdSource) => boolean;
  closeAd: (source: AdSource) => void;
  resetAdActivity: () => void;
};
const AdsContext = createContext<AdsContextValue | null>(null);
const NOOP_AD_ACTIVITY = { isAnyAdOpen: false, openAd: () => false, closeAd: () => undefined, resetAdActivity: () => undefined } as const;

export function AdsProvider({ adapter = noopAdsAdapter, children }: { adapter?: AdsAdapter; children: React.ReactNode }) {
  const [openSources, setOpenSources] = useState<Set<AdSource>>(() => new Set());
  const openSourcesRef = useRef(new Set<AdSource>());
  const openAd = useCallback((source: AdSource) => {
    const current = openSourcesRef.current;
    if (current.size > 0) return false;
    const next = new Set(current).add(source);
    openSourcesRef.current = next;
    setOpenSources(next);
    return true;
  }, []);
  const closeAd = useCallback((source: AdSource) => {
    const current = openSourcesRef.current;
    if (!current.has(source)) return;
    const next = new Set(current);
    next.delete(source);
    openSourcesRef.current = next;
    setOpenSources(next);
  }, []);
  const resetAdActivity = useCallback(() => {
    openSourcesRef.current = new Set();
    setOpenSources(new Set());
  }, []);
  const value = useMemo(() => ({ ...adapter, isAnyAdOpen: openSources.size > 0, openAd, closeAd, resetAdActivity }), [adapter, closeAd, openAd, openSources, resetAdActivity]);
  return <AdsContext.Provider value={value}>{children}</AdsContext.Provider>;
}

export function useAdActivity(): Pick<AdsContextValue, "isAnyAdOpen" | "openAd" | "closeAd" | "resetAdActivity"> {
  return useContext(AdsContext) ?? NOOP_AD_ACTIVITY;
}

export function useInterstitialAd() {
  const value = useContext(AdsContext) ?? { ...noopAdsAdapter, ...NOOP_AD_ACTIVITY };
  const show = useCallback(async (input: InterstitialShowInput) => {
    if (!input.destinationVisible) return false;
    const source: AdSource = `interstitial:${input.taskId}`;
    if (!value.openAd(source)) return false;
    try {
      return await value.interstitial.show({ ...input, onImpression: () => undefined });
    } finally {
      value.closeAd(source);
    }
  }, [value]);
  return { ...value.interstitial, show };
}

export function useBannerAd() {
  const value = useContext(AdsContext) ?? { ...noopAdsAdapter };
  return value.banner;
}

export function useBannerSlot(input: { enabled: boolean; target: HTMLElement | null; taskId: string | null; placement: string }) {
  const banner = useBannerAd();
  const [status, setStatus] = useState<BannerStatus>("unavailable");
  const destroyRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    destroyRef.current?.();
    destroyRef.current = null;
    if (!input.enabled || !input.target || !input.taskId) {
      setStatus("unavailable");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    void banner.attach({
      target: input.target,
      taskId: input.taskId,
      placement: input.placement,
      onStatus: (next) => { if (!cancelled) setStatus(next); },
    }).then((result) => {
      if (cancelled) result.destroy();
      else {
        destroyRef.current = result.destroy;
        setStatus(result.status);
      }
    }).catch(() => { if (!cancelled) setStatus("error"); });
    return () => {
      cancelled = true;
      destroyRef.current?.();
      destroyRef.current = null;
    };
  }, [banner, input.enabled, input.placement, input.target, input.taskId]);
  return { status, visible: status === "loading" || status === "rendered" };
}
